import React from 'react';
import { ArrowUpRight, ArrowDownRight, ArrowRight } from 'lucide-react';
import { DataLayerGraphProps } from '@/types/shared/data-layers.types';
import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import { PlotlyBarChart } from '@/modules/shared/DataLayers';

interface FleetAbuseChartProps extends DataLayerGraphProps {
  onClose?: () => void;
}

export const FleetAbuseChartDataLayer: React.FC<FleetAbuseChartProps> = ({
  graph,
  data,
  loading = false,
  className = '',
}) => {
  // Check if data is array or object with chart data
  const chartItems = React.useMemo(() => {
    if (!data) return [];
    // If data is an array, use it directly
    if (Array.isArray(data)) return data;
    // If data has a chartData or items property
    if (data.chartData) return data.chartData;
    if (data.items) return data.items;
    return [];
  }, [data]);

  // Transform API data: { month: "2025-11-01", average: 6.68 } => { shortMonth: "Nov", value: 6.68 }
  const rawChartData = React.useMemo(() => {
    if (!chartItems || chartItems.length === 0) {
      return [];
    }
    return chartItems.map((item: any) => {
      const date = new Date(item.month || item.id);
      const shortMonth = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      return {
        month: item.month,
        shortMonth,
        value: item.average ?? item.value ?? 0,
        trend: item.trend,
      };
    });
  }, [chartItems]);

  // Get total from API data or calculate from actual data
  const total = React.useMemo(() => {
    // Check if API provides total directly
    if (data && !Array.isArray(data) && data.total !== undefined) {
      return Math.round(data.total);
    }
    if (!rawChartData || rawChartData.length === 0) return 0;
    return Math.round(rawChartData.reduce((sum: number, item: any) => sum + (item.value || 0), 0));
  }, [data, rawChartData]);

  // Get trend from API data or calculate from chart data
  const trend = React.useMemo(() => {
    // Check if API provides trend directly at data level
    if (data && !Array.isArray(data) && data.trend !== undefined) {
      return {
        value: Math.round(data.trend),
        isPositive: data.trend >= 0,
      };
    }
    // Check if any chart item has trend value
    if (rawChartData.length > 0 && rawChartData[0].trend !== undefined) {
      const trendValue = rawChartData[rawChartData.length - 1].trend ?? rawChartData[0].trend;
      return {
        value: Math.round(trendValue),
        isPositive: trendValue >= 0,
      };
    }
    // Fall back to calculating trend from first and last values
    if (!rawChartData || rawChartData.length < 2) return null;
    const firstValue = rawChartData[0].value;
    const lastValue = rawChartData[rawChartData.length - 1].value;
    if (firstValue === 0) return null;
    const percentChange = ((lastValue - firstValue) / firstValue) * 100;
    return {
      value: Math.round(percentChange),
      isPositive: percentChange >= 0,
    };
  }, [data, rawChartData]);

  const cardClasses = `flex flex-col ${className}`;

  if (loading) {
    return (
      <BaseCard
        variant="wrapper"
        title={<span className="text-base">{graph.title || 'Average Event Count per Operator'}</span>}
        classNames={{
          card: cardClasses,
          title: 'flex-shrink-0',
          content: 'flex-1 min-h-0 flex items-center justify-center',
        }}
      >
        <div className="text-gray-500">Loading chart data...</div>
      </BaseCard>
    );
  }

  if (!rawChartData || rawChartData.length === 0) {
    return (
      <BaseCard
        variant="wrapper"
        title={<span className="text-base">{graph.title || 'Average Event Count per Operator'}</span>}
        classNames={{
          card: cardClasses,
          title: 'flex-shrink-0',
          content: 'flex-1 min-h-0 flex items-center justify-center',
        }}
      >
        <div className="text-gray-500 text-sm">No data available</div>
      </BaseCard>
    );
  }

  return (
    <BaseCard
      variant="wrapper"
      title={
        <div className="flex items-center justify-between w-full">
          <span className="text-base">{graph.title || 'Average Event Count per Operator'}</span>
          <div className="flex items-center gap-3">
            {trend && (
              <span className={`flex items-center gap-1 text-sm font-bold ${
                trend.value < 0 ? 'text-emerald-600' : trend.value > 0 ? 'text-rose-600' : 'text-gray-500'
              }`}>
                <span className="text-gray-900 font-bold">Trend:</span>
                {trend.value < 0 && <ArrowDownRight className="w-4 h-4" strokeWidth={3} />}
                {trend.value > 0 && <ArrowUpRight className="w-4 h-4" strokeWidth={3} />}
                {trend.value === 0 && <ArrowRight className="w-4 h-4" strokeWidth={3} />}
                {trend.value < 0 ? '' : '+'}{trend.value}%
              </span>
            )}
            <span className="font-bold text-sm text-gray-900">Total: {total}</span>
          </div>
        </div>
      }
      classNames={{
        card: cardClasses,
        title: 'flex-shrink-0',
        content: 'flex-1 min-h-0',
      }}
    >
      <div className="w-full overflow-hidden">
        <PlotlyBarChart
          data={{
            x: rawChartData.map((d: any) => d.shortMonth),
            y: rawChartData.map((d: any) => d.value),
            color: '#7071F3',
            name: 'Abuse Count',
          }}
          height={160}
          unit="count"
          bargap={0.85}
          showValuesOnBars={true}
        />
      </div>
    </BaseCard>
  );
};

// Export the configuration for this data layer
export const fleetAbuseChartConfig = {
  id: 'fleet-abuse-chart',
  title: 'Average Event Count per Operator',
  type: 'bar' as const,
  unit: 'count',
  customComponent: FleetAbuseChartDataLayer,
  legend: [],
  features: ['gradient', 'values-on-top'],
};
